/**
 * CrossPortalGuard — audit remediation Phase 0.
 *
 * The FoodPulse.net SEO audit flagged that the same React bundle serves
 * multiple portals via hostname routing, which caused a nature/species
 * page (SocialPulse framing) to be indexed under FoodPulse — diluting
 * topic focus and creating duplicate-content risk.
 *
 * This guard runs once per route change. When the visitor is on the
 * FoodPulse host but the path belongs to another portal's vertical
 * (Nature / Weather almanac / Family tree / etc.), we 301-redirect to
 * the correct sibling site so the URL stops appearing in the FoodPulse
 * index. Redirects are hard `window.location.replace` calls so the
 * browser history + share-links stay clean.
 *
 * The mapping is intentionally conservative: only routes that were
 * explicitly called out by the audit as cross-portal contamination get
 * the redirect. Everything else falls through unchanged.
 */
import { useEffect } from "react";
import { useLocation } from "react-router-dom";

// Path prefixes → target host mapping. `test` matches against `pathname`.
const CROSS_PORTAL_RULES = [
  { test: /^\/nature(\/|$)/i, host: "socialpulse.com" },
  { test: /^\/weather(\/|$)/i, host: "socialpulse.com" },
  { test: /^\/family(\/|$)/i, host: "indianfamilytree.com" },
  { test: /^\/family-trees(\/|$)/i, host: "indianfamilytree.com" },
  { test: /^\/global-family-trees(\/|$)/i, host: "indianfamilytree.com" },
  { test: /^\/celebrate(\/|$)/i, host: "socialpulse.com" },
  { test: /^\/obituaries(\/|$)/i, host: "socialpulse.com" },
  { test: /^\/kin-network(\/|$)/i, host: "indianfamilytree.com" },
  { test: /^\/state\//i, host: "indianfamilytree.com" },
];

function currentHost() {
  try {
    return String(window.location.hostname || "").toLowerCase();
  } catch { return ""; }
}

export default function CrossPortalGuard() {
  const { pathname, search, hash } = useLocation();

  useEffect(() => {
    const host = currentHost();
    // Only enforced on FoodPulse hosts — preview URLs, socialpulse.com
    // and indianfamilytree.com keep working as before. Also skip when a
    // developer explicitly opts out with `?portal=stay`.
    const isFoodPulseHost = /foodpulse/i.test(host);
    if (!isFoodPulseHost) return;
    try {
      if (new URLSearchParams(search).get("portal") === "stay") return;
    } catch { /* ignore */ }

    const match = CROSS_PORTAL_RULES.find((r) => r.test.test(pathname));
    if (!match) return;

    const target = `https://${match.host}${pathname}${search}${hash}`;
    // Hard-redirect so search crawlers see a clean 302 → sibling site.
    // (We can't emit a real 301 from client-side JS; a follow-up will
    // add a backend edge redirect for crawlers that don't run JS.)
    window.location.replace(target);
  }, [pathname, search, hash]);

  return null;
}
